/**
 * Account Worker entity DTOs.
 *
 * Mirrors Prisma `AccountWorker` (prisma/schema.prisma:541) — the account-
 * level address-book row. Distinct from `Worker` (per-project) — an
 * AccountWorker can be linked into N projects as N ProjectWorker rows.
 *
 * Web equivalent: `app/(app)/workers/page.tsx` → `AccountWorkersManager`
 * → `/api/account-workers/*`. The mobile mirror at
 * `/api/mobile/v1/account/workers/*` consumes these schemas via
 * `safeParseOrWarn`.
 */
import { z } from "zod";
import { entityIdSchema } from "../common";
import { WorkerRoleSchema, WorkerCertificationDateInputSchema } from "./worker";

/**
 * Cert date string: accepts `YYYY-MM-DD` or ISO datetime, or empty.
 * Same calendar-correct input rule as per-project worker certification dates.
 */
const CertDateString = WorkerCertificationDateInputSchema;

export const AccountWorkerSchema = z.object({
  id: entityIdSchema,
  name: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  company: z.string().nullable(),
  role: WorkerRoleSchema,
  email: z.string().nullable(),
  phone: z.string().nullable(),
  firstAidTrained: z.boolean(),
  fireWardenTrained: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type AccountWorker = z.infer<typeof AccountWorkerSchema>;

/**
 * Server-only strict variant of the response DTO (#2411 batch 1).
 *
 * The mobile app parses `AccountWorkerSchema` off the wire, so it is
 * deliberately NOT `.strict()` — an installed build must tolerate a field
 * the server added after it shipped. On the server the strictness was an
 * over-serialisation guard (the route serialisers build the payload
 * field-by-field and `.parse()` it), so it is re-applied here for the
 * server's own response boundary. Request bodies are unaffected: they are
 * validated by the separate Create/Update schemas below, which stay strict.
 */
export const AccountWorkerStrict = AccountWorkerSchema.strict();

/**
 * POST /api/mobile/v1/account/workers is an UPSERT keyed on the worker's name:
 * a re-add of an existing name takes the UPDATE branch. So an omitted key must
 * stay omitted after parsing — `role`, `firstAidTrained` and `fireWardenTrained`
 * therefore carry NO `.default()` here (a default would be indistinguishable
 * from a value the caller sent, and would overwrite the stored one). The server
 * applies the create defaults (`STAFF`, `false`, `false`) on the CREATE branch
 * only (`lib/account-worker-fields.ts`). Same wire shape for every caller — a
 * client that sends every key behaves exactly as before.
 */
export const AccountWorkerCreateRequest = z
  .object({
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    company: z.string().nullable().optional(),
    role: WorkerRoleSchema.optional(),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    firstAidTrained: z.boolean().optional(),
    fireWardenTrained: z.boolean().optional(),
  })
  .strict()
  .refine(
    (v) => Boolean((v.firstName ?? "").trim() || (v.lastName ?? "").trim()),
    { message: "First or last name is required", path: ["firstName"] },
  );

export type AccountWorkerCreateRequest = z.infer<
  typeof AccountWorkerCreateRequest
>;

/**
 * `expectedUpdatedAt` is the OPTIONAL optimistic-concurrency claim (#2369):
 * matched against the AccountWorker row BEFORE the edit applies or fans out
 * to linked ProjectWorker rows and Site Access identities — a stale claim
 * must leave the whole fan-out untouched. Absent → today's behaviour. A
 * claim, never a written field.
 */
export const AccountWorkerUpdateRequest = z
  .object({
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    company: z.string().nullable().optional(),
    role: WorkerRoleSchema.optional(),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    firstAidTrained: z.boolean().optional(),
    fireWardenTrained: z.boolean().optional(),
    expectedUpdatedAt: z.string().datetime().optional(),
  })
  .strict();

export type AccountWorkerUpdateRequest = z.infer<
  typeof AccountWorkerUpdateRequest
>;

// ─── Certifications ────────────────────────────────────────────────────────

export const WorkerCertificationSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    issuedAt: z.string().datetime().nullable(),
    expiresAt: z.string().datetime(),
    fileUrl: z.string().nullable(),
    createdAt: z.string().datetime(),
    // Row version, carried since #2369 so the cert file attach/detach flow
    // can echo it back as `expectedUpdatedAt` (optional claim on the update
    // request). Additive on a strict wire leaf — installed builds fail the
    // strict parse and fall back to raw via safeParseOrWarn; a client that
    // never sees it sends no claim and the edit behaves exactly as before.
    updatedAt: z.string().datetime().optional(),
  });

export type WorkerCertification = z.infer<typeof WorkerCertificationSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the certification routes serialise through the Strict form.
export const WorkerCertificationStrict = WorkerCertificationSchema.strict();

export const WorkerCertificationCreateRequest = z
  .object({
    // Certification labels are required; normalize surrounding whitespace at
    // the shared request boundary while preserving the interior verbatim.
    name: z.string().trim().min(1),
    issuedAt: CertDateString.nullable().optional(),
    expiresAt: CertDateString,
    fileUrl: z.string().nullable().optional(),
  })
  .strict();

export type WorkerCertificationCreateRequest = z.infer<
  typeof WorkerCertificationCreateRequest
>;

/**
 * PATCH /api/mobile/v1/account/workers/:workerId/certifications/:certId body.
 *
 * Supports in-place certificate detail edits and evidence attach/detach.
 * `null` detaches an evidence file; omitted fields remain unchanged.
 *
 * `expectedUpdatedAt` is the OPTIONAL optimistic-concurrency claim (#2369):
 * matched against the certification row before the file swap (and before the
 * previous file is queued for reclaim). Absent → today's behaviour. A claim,
 * never a written field.
 */
export const WorkerCertificationUpdateRequest = z
  .object({
    name: z.string().trim().min(1).optional(),
    issuedAt: CertDateString.nullable().optional(),
    expiresAt: CertDateString.optional(),
    fileUrl: z.string().nullable().optional(),
    expectedUpdatedAt: z.string().datetime().optional(),
  })
  .strict()
  .refine(
    (value) =>
      (value.name === undefined && value.issuedAt === undefined && value.expiresAt === undefined) ||
      value.expectedUpdatedAt !== undefined,
    { message: "Editing certification details requires expectedUpdatedAt" },
  )
  .refine((value) => value.expiresAt === undefined || value.expiresAt !== "", {
    message: "Expiry date is required",
    path: ["expiresAt"],
  });

export type WorkerCertificationUpdateRequest = z.infer<
  typeof WorkerCertificationUpdateRequest
>;
